import $ from "jquery";

$(function () {
    const sections = document.querySelectorAll(".page-content [id].panel");
    let activeSectionIndicator = "";

    // Track whether the user has interacted before window.load fires.
    // Note: scroll-behavior:auto is set early via an inline <head> script when
    // a URL hash is present, and restored here once async content has settled.
    // Prevents an unwanted re-scroll snap when users begin scrolling manually
    // on slow connections before all images have loaded.
    let userInteracted = false;
    const interactionEvents = ["pointerdown", "wheel", "touchstart", "keydown"] as const;
    const markInteracted = () => {
        if (userInteracted) return;
        userInteracted = true;
    };
    interactionEvents.forEach((evt) =>
        window.addEventListener(evt, markInteracted, { once: false, passive: true, capture: true })
    );
    const activeClass = "text-navy-600/50"; // Store class name in a variable for consistency

    // Function to update the active navigation item
    const updateActiveNavItem = (newActiveId: string) => {
        // Only update if the active section changed
        if (newActiveId === activeSectionIndicator) {
            return;
        }

        // Remove active class from current active item
        // Use CSS.escape to properly escape the slash in the class selector
        const activeNavElement = document.querySelector(`.js-spy-container li a.${CSS.escape(activeClass)}`);
        if (activeNavElement) {
            activeNavElement.classList.remove(activeClass);
        }

        // Set new active section indicator
        activeSectionIndicator = newActiveId;

        // Add active class to new active item
        const newActiveNavElement = document.querySelector(`.js-spy-container [href="#${activeSectionIndicator}"]`);
        if (newActiveNavElement) {
            newActiveNavElement.classList.add(activeClass);
        }
    };

    // Function to determine which section is closest to the viewport
    const determineClosestSection = () => {
        if (sections.length === 0) return;

        // Check if page is scrolled to the top (with small tolerance)
        const scrollTopThreshold = 10; // pixels from top
        if (window.scrollY <= scrollTopThreshold && sections.length > 0) {
            // When at the top of page, highlight the first section
            const firstSection = sections[0] as HTMLElement;
            if (firstSection?.id) {
                updateActiveNavItem(firstSection.id);
                return;
            }
        }

        const viewportHeight = window.innerHeight;
        const viewportMiddle = viewportHeight / 3; // Use top third as reference point
        const documentHeight = document.documentElement.scrollHeight;
        const bottomThreshold = 50; // pixels from bottom
        const isNearBottom = window.innerHeight + window.scrollY >= documentHeight - bottomThreshold;

        // Special case for bottom of page - highlight the last section
        if (isNearBottom) {
            const lastSection = sections[sections.length - 1] as HTMLElement;
            if (lastSection?.id) {
                updateActiveNavItem(lastSection.id);
                return;
            }
        }

        let closestSection: Element | null = null;
        let closestDistance = Infinity;
        const visibleSections: Element[] = [];

        // Collect all visible sections
        sections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            // Consider a section visible if any part of it is in the viewport
            if (rect.top < viewportHeight && rect.bottom > 0) {
                visibleSections.push(section);
            }

            // Calculate distance from viewport reference point for proximity
            const sectionMiddle = rect.top + rect.height / 2;
            const distance = Math.abs(sectionMiddle - viewportMiddle);

            // Update closest section if this one is closer
            if (distance < closestDistance) {
                closestDistance = distance;
                closestSection = section;
            }
        });

        // If we have visible sections but the closest section is not visible,
        // prioritize the first visible section
        if (visibleSections.length > 0 && closestSection) {
            const closestSectionElement = closestSection as HTMLElement;
            const closestRect = closestSectionElement.getBoundingClientRect();
            if (closestRect.bottom <= 0 || closestRect.top >= viewportHeight) {
                // Closest section is not visible, use the top-most visible section
                const firstVisibleSection = visibleSections[0];
                if (firstVisibleSection) closestSection = firstVisibleSection;
            }
        }

        // If we have determined a section to highlight
        if (closestSection) {
            const sectionElement = closestSection as HTMLElement;
            const id = sectionElement.id;
            if (id) {
                updateActiveNavItem(id);
            }
        }
    };

    // Handle click events on navigation items
    const navContainer = document.querySelector(".js-spy-container");
    if (navContainer) {
        navContainer.addEventListener("click", (event) => {
            // Handle the click event
            const target = event.target as HTMLElement;
            if (target.tagName === "A" || target.closest("a")) {
                const link = target.tagName === "A" ? target : target.closest("a");
                if (link) {
                    const href = link.getAttribute("href");
                    if (href?.startsWith("#")) {
                        // Prevent default anchor link behavior
                        event.preventDefault();

                        const targetId = href.substring(1);
                        const targetElement = document.getElementById(targetId);

                        if (targetElement) {
                            // Explicitly pass smooth so it works regardless of whether
                            // the CSS scroll-behavior override is currently active.
                            targetElement.scrollIntoView({ behavior: "smooth", block: "start" });

                            // Update the active nav item and URL after scrolling
                            updateActiveNavItem(targetId);
                            // Update URL without causing a jump
                            history.pushState(null, "", href);
                        }
                    }
                }
            }
        });
    }

    // Wait for page to load and any jump links to resolve before initial check
    window.addEventListener("load", () => {
        if (!window.location.hash) {
            determineClosestSection();
            return;
        }

        const targetId = window.location.hash.substring(1);
        const targetElement = document.getElementById(targetId);

        if (!targetElement) {
            determineClosestSection();
            return;
        }

        if (!userInteracted) {
            targetElement.scrollIntoView({ behavior: "instant", block: "start" });
        }
        updateActiveNavItem(targetId);

        // The main source of layout shift on these pages is async content from
        // Vue search apps (documentation, partner-boards, companion-modules).
        // They start at min-h-40 (160px) and expand by 600–900px each after their
        // API calls complete — which happens AFTER window.load fires.
        // A ResizeObserver on the page-content container detects these expansions
        // and re-scrolls the user back to their intended anchor, as long as they
        // haven't started scrolling manually (userInteracted guard).
        const pageContent = document.querySelector(".page-content");
        if (!pageContent) {
            // No async content to watch — restore smooth scroll immediately.
            document.documentElement.style.removeProperty("scroll-behavior");
            return;
        }

        // Once content has settled, remove the inline scroll-behavior:auto
        // override so that CSS scroll-behavior:smooth is restored for the rest
        // of the page session.
        const restoreSmoothScroll = () => document.documentElement.style.removeProperty("scroll-behavior");

        const ro = new ResizeObserver(() => {
            if (userInteracted) {
                ro.disconnect();
                restoreSmoothScroll();
                return;
            }
            const el = document.getElementById(targetId);
            if (el) {
                el.scrollIntoView({ behavior: "instant", block: "start" });
                updateActiveNavItem(targetId);
            }
        });

        ro.observe(pageContent);

        // Stop watching after 10 seconds — all async content should have
        // rendered well within this window.
        setTimeout(() => {
            ro.disconnect();
            restoreSmoothScroll();
        }, 10000);
    });

    // Listen for scroll events to update the active section
    window.addEventListener(
        "scroll",
        () => {
            // Use requestAnimationFrame for better performance
            window.requestAnimationFrame(determineClosestSection);
        },
        { passive: true }
    );
});
